/**
 * SEO — small wrapper around react-helmet-async that ensures every public
 * page has a consistent title, description, canonical URL, Open Graph
 * tags, Twitter card and (optional) JSON-LD structured data.
 *
 * Why a wrapper instead of dropping <Helmet> directly on every page:
 *   - Centralises the TOVIAS brand defaults (site name, locale,
 *     fallback image) so we don't repeat them on every route.
 *   - Builds the canonical URL automatically from the current path.
 *   - Forces both og:* and twitter:* tags whenever you set a title or
 *     description, so we never accidentally publish a page with the
 *     generic landing OG card.
 *   - Accepts a `jsonLd` prop for structured data (Article,
 *     BreadcrumbList, Organization…) which Google reads and uses for
 *     rich results.
 *
 * Usage:
 *   <SEO
 *     title="Lisboa en familia · ruta de 5 días"
 *     description="Itinerario probado con niños…"
 *     image="https://tovias.app/.../cover.jpg"
 *     path="/r/demo-share-lisboa"
 *     jsonLd={{ '@context':'https://schema.org', '@type':'Article', ... }}
 *   />
 */
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://tovias.app';
const DEFAULT_TITLE = 'TOVÍAS — Organiza viajes en grupo sin caos de WhatsApp';
const DEFAULT_DESCRIPTION =
  'Ten todos tus viajes organizados con Tovías: participantes, gastos, reservas, entradas, fotos e itinerarios, todo en un solo lugar.';
const DEFAULT_IMAGE = `${SITE_URL}/tovias-og.jpg`;

const SEO = ({
  title,
  description,
  image,
  path,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title
    ? `${title} · TOVÍAS`
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const img = image || DEFAULT_IMAGE;
  // Canonical URL: prefer the explicit prop, else fall back to the
  // current location so we always have a stable, indexable URL.
  const canonical = (() => {
    if (path) return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
    if (typeof window !== 'undefined') {
      return `${SITE_URL}${window.location.pathname}`;
    }
    return SITE_URL;
  })();

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content="TOVÍAS" />
      <meta property="og:locale" content="es_ES" />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
